import { Animation, EventTracker } from '@devexpress/dx-react-chart';
import {
  ArgumentAxis,
  BarSeries,
  Chart,
  ValueAxis,
  Tooltip as DxTooltip,
} from '@devexpress/dx-react-chart-material-ui';
import { Tooltip } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { TooltipOverlay } from 'components';
import Info from '@material-ui/icons/InfoOutlined';
import useWindowDimensions from 'hooks/useWindowDimensions';
import React from 'react';
import useStyles from './style';
import { IDailyBulletinChartProps, ITooltipContentProps } from './types';

const ChartDailyBulletin = ({ data, total }: IDailyBulletinChartProps) => {
  const { width } = useWindowDimensions();
  const classes = useStyles();

  const mustAbbreviate = React.useMemo(() => {
    // largura aproximada de uma tela fullhd
    return width < 1900;
  }, [width]);

  const pallet = React.useMemo(() => data.map(item => item.color), [data]);

  const PointComponent = React.useCallback(
    ({ ...props }) => {
      if (props.value) {
        return (
          <>
            <BarSeries.Point {...props} color={pallet[props.index]} />
            <Chart.Label
              // O gráfico provê uma função para retornar o ponto de Y e X. Subtraindo/Somando um valor em px para afastar/aproximar a label da barra
              y={
                props.value
                  ? props.scales.valScale(props.value) - 10
                  : props.arg
              }
              x={props.arg}
              textAnchor="middle"
              className={classes.barLabel}
            >
              {props.value}
            </Chart.Label>
          </>
        );
      }
      return <BarSeries.Point {...props} />;
    },
    [classes.barLabel, pallet],
  );

  const axiosLabelComponent = React.useCallback(
    (props: ArgumentAxis.LabelProps) => {
      let abrev = props.text;
      if (mustAbbreviate && typeof abrev === 'string' && abrev.length >= 9) {
        abrev = `${abrev.substring(0, 7)}...`;
      }
      return (
        <Tooltip title={props.text}>
          <ArgumentAxis.Label
            className={classes.labelItem}
            {...props}
            text={abrev}
          />
        </Tooltip>
      );
    },
    [classes.labelItem, mustAbbreviate],
  );

  const TooltipContent = ({ targetItem, ...props }: ITooltipContentProps) => {
    const { point } = targetItem;
    const { maintenenceType, total: serieTotal } = data[point];
    const percentageValue = ((serieTotal * 100) / total).toFixed(2);

    const message = `${maintenenceType}: ${total}; ${percentageValue}%`;

    return (
      <DxTooltip.Content {...props} targetItem={targetItem} text={message} />
    );
  };

  if (!data || total <= 0) {
    return (
      <div className={classes.noData}>
        <Info />
        <p>Sem dados disponíveis</p>
      </div>
    );
  }

  return (
    <Paper className={classes.dailyChart}>
      <Chart data={data} height={80} width={370}>
        <ArgumentAxis labelComponent={axiosLabelComponent} />
        <ValueAxis showGrid={false} showLine={false} showLabels={false} />
        <BarSeries
          valueField="total"
          argumentField="maintenenceType"
          pointComponent={PointComponent}
        />
        <Animation />
        <EventTracker />

        <DxTooltip
          overlayComponent={TooltipOverlay}
          contentComponent={TooltipContent}
        />
      </Chart>
    </Paper>
  );
};

export default ChartDailyBulletin;
